import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineArrowRight } from 'react-icons/ai';
import { RiWifiLine, RiWifiOffLine } from 'react-icons/ri';
import styled from 'styled-components';
import { wrapIterator } from '../../../shared/helper/IterableIteratorWrapper';
import { clientId } from '../../data/clientId';
import { timeDurationToString } from '../../helper/displayHelpers';
import { ConnectionStatus, editorStore } from '../../stores/EditorStore';
import { networkDiagnosticsStore } from '../../stores/NetworkDiagnosticsStore';
import { Heading1Base, Heading2Base } from '../shared/Heading';
import { PopupWindow } from '../shared/PopupComponents';
const ScrollablePopupWindow = styled(PopupWindow).withConfig({
  displayName: "NetworkDiagnostics__ScrollablePopupWindow",
  componentId: "sc-qrqec4-0"
})(["max-height:100%;overflow:auto;font-size:0.8em;"]);
const Title = styled(Heading1Base).withConfig({
  displayName: "NetworkDiagnostics__Title",
  componentId: "sc-qrqec4-1"
})(["text-align:center;padding-bottom:0.3em;font-size:x-large;"]);
const Table = styled.table.withConfig({
  displayName: "NetworkDiagnostics__Table",
  componentId: "sc-qrqec4-2"
})(["width:100%;border-collapse:collapse;th,td{padding:5px;}th{text-align:right;}tr.warn{color:red;}"]);
const Header = styled(Heading2Base).withConfig({
  displayName: "NetworkDiagnostics__Header",
  componentId: "sc-qrqec4-3"
})(["text-align:center;border-bottom:1px solid black;"]);

const Category = ({
  title,
  children
}) => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    colSpan: 2
  }, /*#__PURE__*/React.createElement(Header, null, title))), children);
};

const Spacer = () => {
  return /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null));
};

const CounterWithTimestampDisplay = observer(({
  counter
}) => {
  if (counter.count === 0) {
    return /*#__PURE__*/React.createElement("span", null, "0");
  }

  const {
    count,
    secondsSinceLastTimestampPrecision1000: totalSeconds
  } = counter;
  return /*#__PURE__*/React.createElement("span", null, count, "  (", timeDurationToString(totalSeconds, false), ")");
});
const CounterMapDisplay = observer(({
  title,
  counterMap,
  warn
}) => {
  if (counterMap.size === 0) return null;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spacer, null), /*#__PURE__*/React.createElement(Category, {
    title: title
  }, wrapIterator(counterMap.entries()).map(([reason, counter]) => /*#__PURE__*/React.createElement("tr", {
    key: reason,
    className: warn ? "warn" : undefined
  }, /*#__PURE__*/React.createElement("th", null, reason), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(CounterWithTimestampDisplay, {
    counter: counter
  }))))));
});
const PingerDisplay = observer(({
  pinger
}) => {
  useEffect(() => {
    pinger.ping().catch(() => {});
  }, []);
  return /*#__PURE__*/React.createElement(React.Fragment, null, pinger.success.count > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, pinger.title, ": Success"), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(CounterWithTimestampDisplay, {
    counter: pinger.success
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null), /*#__PURE__*/React.createElement("td", null, pinger.successRoundTripTimeMSArray.join(", "), " ms"))), pinger.failure.count > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", {
    className: "warn"
  }, /*#__PURE__*/React.createElement("th", null, pinger.title, ": Failure"), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(CounterWithTimestampDisplay, {
    counter: pinger.failure
  }))), /*#__PURE__*/React.createElement("tr", {
    className: "warn"
  }, /*#__PURE__*/React.createElement("th", null), /*#__PURE__*/React.createElement("td", null, pinger.latestFailureReason))));
});

const NetworkInformationDisplay = () => {
  const getRtt = () => {
    var _connection;

    return navigator && ((_connection = navigator["connection"]) === null || _connection === void 0 ? void 0 : _connection.rtt);
  };

  const getDownlink = () => {
    var _connection2;

    return navigator && ((_connection2 = navigator["connection"]) === null || _connection2 === void 0 ? void 0 : _connection2.downlink);
  };

  const [rtt, setRtt] = useState(getRtt());
  const [downlink, setDownlink] = useState(getDownlink());
  useEffect(() => {
    let timeout;

    const refreshValuesLooping = () => {
      setRtt(getRtt());
      setDownlink(getDownlink());
      timeout = setTimeout(refreshValuesLooping, 1000);
    };

    refreshValuesLooping();
    return () => clearTimeout(timeout);
  }, []);
  if (!rtt && !downlink) return null;
  return /*#__PURE__*/React.createElement(React.Fragment, null, downlink && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Downlink"), /*#__PURE__*/React.createElement("td", null, downlink, " mbits/s")), rtt && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Round-trip time"), /*#__PURE__*/React.createElement("td", null, rtt, " ms")));
};

export const NetworkDiagnostics = observer(() => {
  const {
    onlineStatus,
    switchedToOfflineCounter,
    switchedToOnlineCounter,
    editorClientConnectedCounter,
    editorClientDisconnectedCounter,
    editorClientDisconnectReasons,
    editorClientConnectErrors,
    pingers
  } = networkDiagnosticsStore;
  const {
    t
  } = useTranslation();
  return /*#__PURE__*/React.createElement(ScrollablePopupWindow, {
    onClick: e => e.stopPropagation()
  }, /*#__PURE__*/React.createElement(Title, null, t("editor.network_diagnostics")), /*#__PURE__*/React.createElement(Table, null, /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement(Category, {
    title: "General"
  }, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Client ID"), /*#__PURE__*/React.createElement("td", null, clientId)), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("td", null, onlineStatus ? "Online" : "Disconnected")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, /*#__PURE__*/React.createElement(AiOutlineArrowRight, null), " ", /*#__PURE__*/React.createElement(RiWifiLine, null)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(CounterWithTimestampDisplay, {
    counter: switchedToOnlineCounter
  }))), switchedToOfflineCounter.count > 0 && /*#__PURE__*/React.createElement("tr", {
    className: "warn"
  }, /*#__PURE__*/React.createElement("th", null, /*#__PURE__*/React.createElement(AiOutlineArrowRight, null), " ", /*#__PURE__*/React.createElement(RiWifiOffLine, null)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(CounterWithTimestampDisplay, {
    counter: switchedToOfflineCounter
  }))), /*#__PURE__*/React.createElement(NetworkInformationDisplay, null)), /*#__PURE__*/React.createElement(Spacer, null), /*#__PURE__*/React.createElement(Category, {
    title: "socket.io Client"
  }, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("td", null, ConnectionStatus[editorStore.connectionStatus])), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, /*#__PURE__*/React.createElement(AiOutlineArrowRight, null), " ", /*#__PURE__*/React.createElement(RiWifiLine, null)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(CounterWithTimestampDisplay, {
    counter: editorClientConnectedCounter
  }))), editorClientDisconnectedCounter.count > 0 && /*#__PURE__*/React.createElement("tr", {
    className: "warn"
  }, /*#__PURE__*/React.createElement("th", null, /*#__PURE__*/React.createElement(AiOutlineArrowRight, null), " ", /*#__PURE__*/React.createElement(RiWifiOffLine, null)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(CounterWithTimestampDisplay, {
    counter: editorClientDisconnectedCounter
  })))), /*#__PURE__*/React.createElement(CounterMapDisplay, {
    title: "Disconnect Reasons",
    counterMap: editorClientDisconnectReasons,
    warn: true
  }), /*#__PURE__*/React.createElement(CounterMapDisplay, {
    title: "Connect Errors",
    counterMap: editorClientConnectErrors,
    warn: true
  }), /*#__PURE__*/React.createElement(Category, {
    title: "Pings"
  }, pingers.map(pinger => /*#__PURE__*/React.createElement(PingerDisplay, {
    key: pinger.url,
    pinger: pinger
  }))))));
});